import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  defer,
  finalize,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  CacheService,
  CacheStorage,
  SP_VERSIONS_PATH,
  SP_VERSION_KEY,
  SpVersionService,
} from '../services/cache';

/**
 * Per-request sozlamalar. So'rovga `context` orqali beriladi:
 *
 *   http.post(url, body, { context: withHttpCache({ storage: 'session', ttl: 600_000 }) })
 *   http.get(url, { context: withHttpCache({ skip: true }) })
 */
export interface HttpCacheOptions {
  /** TTL cache'ni majburan yoqish/o'chirish (default: directory endpoint'lar uchun avtomatik). */
  cache?: boolean;
  /** Cache'langan javob necha ms saqlanadi (default 5 daqiqa). */
  ttl?: number;
  /** Qayerda saqlash: 'memory' (default, F5 da o'chadi) | 'session' | 'local' | 'indexeddb'. */
  storage?: CacheStorage;
  /** Ushbu so'rov uchun dedup + cache'ni butunlay o'tkazib yuborish. */
  skip?: boolean;
}

export const HTTP_CACHE = new HttpContextToken<HttpCacheOptions>(() => ({}));

export function withHttpCache(
  options: HttpCacheOptions,
  context: HttpContext = new HttpContext(),
): HttpContext {
  return context.set(HTTP_CACHE, options);
}

/**
 * Oddiy read so'rovlari uchun (memory).
 * RAM kesh F5, tab yopish, logout va har qanday mutatsiyada baribir tozalanadi,
 * shuning uchun muddat uzunroq bo'lishi mumkin. Yagona xavf: F5 siz uzoq
 * ishlagan sessiyada boshqa tab/admin/SignalR o'zgartirsa — shu muddatgacha eski.
 */
const DEFAULT_TTL = 5 * 60 * 1000;
/**
 * Ma'lumotnoma (sp*) endpoint'lari — IndexedDB'da 7 kun.
 * Asosiy invalidatsiya `spversions/get-all` orqali versiya bo'yicha; TTL faqat zaxira.
 */
const DIRECTORY_TTL = 7 * 24 * 60 * 60 * 1000;
const DIRECTORY_STORAGE: CacheStorage = 'indexeddb';

/**
 * Hech qachon keshlanmaydigan URL bo'laklari (jonli/vaqtga bog'liq ma'lumot).
 * Kerak bo'lsa shu ro'yxatni to'ldiring yoki so'rovda `withHttpCache({ skip: true })`.
 */
const NEVER_CACHE = [
  '/time',
  '/timer',
  '/remaining',
  '/ping',
  '/heartbeat',
  '/otp',
  '/captcha',
  SP_VERSIONS_PATH,
];

/** URL oxiri shu ko'rinishlardan biri bo'lsa — bu "o'qish" (read) so'rovi, dedup xavfsiz. */
const READ_SUFFIXES = [
  'get-paged',
  'get-all',
  'get-list',
  'getbyid',
  'get-by-id',
  'infobyinn',
  'paged',
  'list',
];

/** Uzoq yashaydigan ma'lumotnoma (directory) endpoint'lari — TTL cache uchun. */
const DIRECTORY_SEGMENTS = [
  'spregions',
  'spdistricts',
  'spschoolgrades',
  'spschoolsubjects',
  'spstudyyears',
  'spinstitutions',
  'spinstitutionclasses',
  'sporganizationtypes',
  'spschoollegalforms',
  'spschooltypes',
  'spschoolmanagementforms',
  'sptranslations',
  'splanguages',
  'spanswersheettemplate',
  'roles',
  'permissions',
];

const HOUR = 60 * 60 * 1000;

const VARIANT_FILES_PATTERN =
  /\/variant\/get-(question|answersheet)-files\/[^/]+$/;

interface FileCacheRule {
  /** `normalizeUrl` natijasiga (kichik harf, query'siz) qo'llanadi. */
  pattern: RegExp;
  method: 'GET' | 'POST';
  storage: CacheStorage;
  ttl: number;
}

/**
 * Fayl (blob) javoblari uchun kesh qoidalari.
 *  - `id` bo'yicha o'zgarmas fayllar → IndexedDB (F5 dan keyin ham qoladi), 24 soat.
 *  - Qayta generatsiya qilinishi mumkin bo'lganlar → memory: har qanday mutatsiya
 *    (generatsiya, saqlash) memory keshni tozalaydi, eski fayl qaytib kelmaydi.
 */
const FILE_CACHE_RULES: FileCacheRule[] = [
  {
    pattern: /\/(files|publicfiles|resourcefiles)\/[^/]+\/download$/,
    method: 'GET',
    storage: 'indexeddb',
    ttl: 24 * HOUR,
  },
  {
    pattern: /\/files\/download-files$/,
    method: 'GET',
    storage: 'indexeddb',
    ttl: 24 * HOUR,
  },
  {
    // Variant o'zgartirilsa (`/variant/` ga mutatsiya) darhol o'chiriladi — invalidateVariantFiles.
    pattern: VARIANT_FILES_PATTERN,
    method: 'GET',
    storage: 'indexeddb',
    ttl: 6 * HOUR,
  },
  {
    pattern: /\/dtm\/download-answersheets\/[^/]+$/,
    method: 'POST',
    storage: 'memory',
    ttl: 10 * 60 * 1000,
  },
  {
    pattern: /\/examseating\/download-participant-list-by-group\/[^/]+$/,
    method: 'GET',
    storage: 'memory',
    ttl: 10 * 60 * 1000,
  },
];

/** IndexedDB'dagi fayl keshi kalitlari prefiksi — hajmni cheklash (prune) uchun. */
const FILE_KEY_PREFIX = 'file:';
/** IndexedDB'da saqlanadigan fayllar soni chegarasi; oshsa eng eskilari o'chiriladi. */
const FILE_CACHE_MAX_ENTRIES = 300;
const FILE_PRUNE_EVERY_WRITES = 20;
let fileWritesSincePrune = 0;

/** Fayl javobida saqlab qolinadigan sarlavhalar (fayl nomi va turi uchun). */
const FILE_HEADERS = ['content-type', 'content-disposition'];

/** Nomi bo'yicha yozuvchi amal — "get" so'zi bo'lsa ham o'qish hisoblanmaydi. */
const WRITE_WORDS =
  /(create|update|delete|remove|add|save|set|upload|generate|confirm|send|change|import|attach|approve|reject)/;

/**
 * `READ_SUFFIXES` ga tushmaydigan, lekin nomi bo'yicha o'qish bo'lgan POST'lar
 * (`variant/get-stages`, `judges/getall-judges`, `examseating/stages-get-paged` ...).
 * Ular keshlanmaydi, lekin mutatsiya kabi memory keshni ham tozalamaydi.
 */
function isReadLikePost(req: HttpRequest<unknown>): boolean {
  if (req.method !== 'POST') {
    return false;
  }
  const name = normalizeUrl(req.url)
    .split('/')
    .filter((seg) => seg && !/^[0-9a-f-]+$/.test(seg))
    .pop();
  if (!name || WRITE_WORDS.test(name)) {
    return false;
  }
  return /^get/.test(name) || /-get(-|$)/.test(name) || /paged/.test(name);
}

function fileRuleOf(req: HttpRequest<unknown>): FileCacheRule | undefined {
  if (req.responseType !== 'blob') {
    return undefined;
  }
  const url = normalizeUrl(req.url);
  return FILE_CACHE_RULES.find(
    (rule) => rule.method === req.method && rule.pattern.test(url),
  );
}

/** localStorage/IndexedDB ga yozish uchun HttpResponse ning oddiy snapshot'i. */
interface ResponseSnapshot {
  body: unknown;
  status: number;
  statusText: string;
  url: string | null;
  /** javob body'sidagi `version` — `sp*` uchun invalidatsiya nazorati */
  v?: number;
  /** fayl javoblari uchun `content-type`, `content-disposition` */
  h?: Record<string, string>;
}

/** Hozir uchayotgan (pending) so'rovlar — bir xil so'rovlar shu bittasiga ulanadi (faqat memory). */
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

function normalizeUrl(url: string): string {
  return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
}

function isReadRequest(req: HttpRequest<unknown>): boolean {
  if (req.method === 'GET' || req.method === 'HEAD') {
    return true;
  }
  if (req.method !== 'POST') {
    return false;
  }
  // Fayl yuklab olish POST'i (masalan barcha javob varaqalari ZIP) — mutatsiya emas.
  if (fileRuleOf(req)) {
    return true;
  }
  const url = normalizeUrl(req.url);
  return READ_SUFFIXES.some((s) => url.endsWith('/' + s));
}

/** So'rov URL'idagi ma'lumotnoma segmenti (`spregions`, `roles`, ...) yoki undefined. */
function directorySegmentOf(req: HttpRequest<unknown>): string | undefined {
  return normalizeUrl(req.url)
    .split('/')
    .find((seg) => DIRECTORY_SEGMENTS.includes(seg));
}

/** Keshlash uchun umuman yaroqsiz so'rovmi (jonli ma'lumot, blob, va h.k.). */
function isNonCacheable(req: HttpRequest<unknown>): boolean {
  if (req.responseType !== 'json') {
    return true; // blob / text / arraybuffer — keshlamaymiz
  }
  const url = normalizeUrl(req.url);
  return NEVER_CACHE.some((frag) => url.includes(frag));
}

/** Javob keshlashga yaroqlimi (2xx + xato body'siz). */
function isCacheableResponse(res: HttpResponse<unknown>): boolean {
  if (res.status < 200 || res.status >= 300) {
    return false;
  }
  const body = res.body as { error?: unknown } | null;
  return !(body && typeof body === 'object' && body.error);
}

function buildKey(req: HttpRequest<unknown>, isFile: boolean): string {
  const body = req.body ? JSON.stringify(req.body) : '';
  const prefix = isFile ? FILE_KEY_PREFIX : 'http:';
  return `${prefix}${req.method}:${req.urlWithParams}:${body}`;
}

function toSnapshot(
  res: HttpResponse<unknown>,
  isFile: boolean,
): ResponseSnapshot {
  const body = res.body as { version?: number } | null;
  const snap: ResponseSnapshot = {
    body: res.body,
    status: res.status,
    statusText: res.statusText,
    url: res.url,
    v: typeof body?.version === 'number' ? body.version : undefined,
  };
  if (isFile) {
    snap.h = {};
    for (const name of FILE_HEADERS) {
      const value = res.headers.get(name);
      if (value) {
        snap.h[name] = value;
      }
    }
  }
  return snap;
}

function fromSnapshot(snap: ResponseSnapshot): HttpResponse<unknown> {
  return new HttpResponse({
    body: snap.body,
    status: snap.status,
    statusText: snap.statusText,
    url: snap.url ?? undefined,
    headers: snap.h ? new HttpHeaders(snap.h) : undefined,
  });
}

/** Variant savollari / javob varaqasi fayllarini IndexedDB keshidan o'chiradi. */
function invalidateVariantFiles(cache: CacheService): Promise<void> {
  return cache.removeWhere(
    'indexeddb',
    (key) =>
      key.includes(FILE_KEY_PREFIX) &&
      /\/variant\/get-(question|answersheet)-files\//.test(key.toLowerCase()),
  );
}

/** IndexedDB'dagi fayl keshini har N ta yozuvda cheklab turadi (eng eskilari o'chadi). */
function pruneFileCache(cache: CacheService): void {
  if (++fileWritesSincePrune < FILE_PRUNE_EVERY_WRITES) {
    return;
  }
  fileWritesSincePrune = 0;
  void cache.prune('indexeddb', FILE_KEY_PREFIX, FILE_CACHE_MAX_ENTRIES);
}

/**
 * Read so'rovlari (GET + `/get-paged`, `/get-all`, `/list` ... bilan tugagan POST):
 *  1. Dedup — bir vaqtda ketgan bir xil so'rovlar bitta tarmoq chaqiruviga birlashadi.
 *  2. Kesh:
 *     - directory (sp*) → IndexedDB, 7 kun; `spversions/get-all` versiyasi
 *       o'zgarganда keyingi sahifa yuklanишида (F5) avtomatik qayta yuklanadi
 *     - boshqa hamma read → memory, 5 daqiqa (F5/mutatsiya/logout baribir tozalaydi)
 * Fayllar (blob) faqat FILE_CACHE_RULES'dagi endpoint'lar uchun keshlanadi.
 * Keshlanmaydi: qolgan blob/text javoblar, 2xx bo'lmagan yoki `body.error` li javoblar,
 * NEVER_CACHE ro'yxatidagi URL'lar, `withHttpCache({ skip: true })` berilganlar.
 * Mutatsiya memory kesh'ni (directory mutatsiyasi IndexedDB'ni ham) tozalaydi.
 * Profildagi "keshni tozalash" tugmasi va logout `CacheService.clear()` ni chaqiradi.
 */
export const httpCacheInterceptor: HttpInterceptorFn = (req, next) => {
  const cache = inject(CacheService);
  const spVersion = inject(SpVersionService);
  const opts = req.context.get(HTTP_CACHE);
  const segment = directorySegmentOf(req);
  const directory = segment !== undefined;
  const fileRule = fileRuleOf(req);
  const read = !opts.skip && isReadRequest(req);

  if (!read) {
    if (
      !opts.skip &&
      !isReadLikePost(req) &&
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)
    ) {
      if (normalizeUrl(req.url).includes('/variant/')) {
        void invalidateVariantFiles(cache);
      }
      // Mutatsiya — tez o'zgaradigan memory kesh har doim tozalanadi.
      void cache.clear('memory');
      // Doimiy (IndexedDB) directory kesh faqat directory'ning o'zi
      // o'zgartirilganda tozalanadi — oddiy ariza/CRUD unga tegmaydi.
      if (directory) {
        void cache.clear(DIRECTORY_STORAGE);
      }
    }
    return next(req);
  }

  const key = buildKey(req, !!fileRule);
  const shouldCache =
    opts.cache ?? (directory || !!fileRule || !isNonCacheable(req));
  const ttl =
    opts.ttl ?? fileRule?.ttl ?? (directory ? DIRECTORY_TTL : DEFAULT_TTL);
  const storage =
    opts.storage ??
    fileRule?.storage ??
    (directory ? DIRECTORY_STORAGE : 'memory');

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const network$ = next(req).pipe(
    tap((event) => {
      if (
        shouldCache &&
        event instanceof HttpResponse &&
        isCacheableResponse(event)
      ) {
        void cache
          .set(key, toSnapshot(event, !!fileRule), { storage, ttl })
          .then(() => {
            if (fileRule && storage === 'indexeddb') {
              pruneFileCache(cache);
            }
          });
      }
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  // `sp*` uchun: kesh yozuvining versiyasini `spversions/get-all` bilan solishtiramiz.
  const versionKey = segment ? SP_VERSION_KEY[segment] : undefined;

  const source$: Observable<HttpEvent<unknown>> = shouldCache
    ? defer(() => {
        const gate$ = versionKey ? spVersion.ensureFresh() : of(undefined);
        return gate$.pipe(
          switchMap(() => from(cache.get<ResponseSnapshot>(key, storage))),
          switchMap((hit) => {
            if (!hit) {
              return network$;
            }
            if (segment && versionKey) {
              const current = spVersion.versionFor(segment);
              if (current !== undefined && current !== hit.v) {
                return network$; // backend versiyani oshirgan — qayta yuklaymiz
              }
            }
            return of(fromSnapshot(hit));
          }),
        );
      })
    : network$;

  const stream = source$.pipe(finalize(() => inFlight.delete(key)));
  inFlight.set(key, stream);
  return stream;
};
